import { cn } from '@/utils/ui/styles'

import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip'

import { useTruncationDetection } from '../hooks/use-truncation-detection'

type TruncatedTextProps = {
	children: React.ReactNode
	/** Custom tooltip content. Defaults to children */
	tooltipContent?: React.ReactNode
	/** Tooltip position. Default: bottom */
	tooltipSide?: 'top' | 'bottom' | 'left' | 'right'
	/** Tooltip delay in ms. Default: 150 */
	tooltipDelay?: number
	/** Additional class names */
	className?: string
	/** Element to render. Default: p */
	as?: 'p' | 'span' | 'div' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

/**
 * Text that truncates with ellipsis and shows tooltip only when truncated
 *
 * @example
 * <TruncatedText className="w-[200px]">
 *   This is a very long text that will be truncated...
 * </TruncatedText>
 *
 * @example
 * // With custom tooltip
 * <TruncatedText tooltipContent="Full description here" tooltipSide="top">
 *   Short text
 * </TruncatedText>
 */
export function TruncatedText({
	children,
	tooltipContent,
	tooltipSide = 'bottom',
	tooltipDelay = 150,
	className,
	as: Element = 'p',
}: TruncatedTextProps) {
	const { ref, isTruncated } = useTruncationDetection<HTMLElement>()

	const textElement = (
		<Element
			ref={ref as React.RefObject<HTMLParagraphElement>}
			className={cn('w-full cursor-default truncate', className)}
		>
			{children}
		</Element>
	)

	if (!isTruncated) {
		return textElement
	}

	return (
		<Tooltip delay={tooltipDelay}>
			<TooltipTrigger render={textElement} />
			<TooltipContent
				align="center"
				side={tooltipSide}
				className="max-w-md wrap-break-word"
			>
				{tooltipContent ?? children}
			</TooltipContent>
		</Tooltip>
	)
}
